import React, { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Alert,
  Button,
  Card,
  CardContent,
  Grid,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CardMembershipIcon from "@mui/icons-material/CardMembership";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import GroupsIcon from "@mui/icons-material/Groups";
import HandshakeIcon from "@mui/icons-material/Handshake";
import InsightsIcon from "@mui/icons-material/Insights";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import BrandSpinner from "../../shared/components/BrandSpinner";
import api from "../../services/api/axios";
import { BRAND_BLUE, BRAND_GREEN } from "../../theme/colors";
import {
  ACTIONS_HEADER_BTN_SX,
  ACTION_MENU_ICON_SX,
  ACTION_MENU_ICON_TONES,
  ACTION_MENU_PAPER_SX,
} from "../../theme/buttonstyles";
import { FILTER_STYLES } from "../../theme/filterstyles";

const PAGE_BG = "#f7f9fc";

const DATE_FILTER_FIELD_SX = {
  ...FILTER_STYLES.formControl,
  flex: 1,
  minWidth: 150,
  "& .MuiPickersInputBase-root, & .MuiPickersOutlinedInput-root": {
    height: 42,
    fontSize: "0.95rem",
    color: BRAND_BLUE,
    borderRadius: 3,
    background: "linear-gradient(180deg, rgba(231,240,218,0.25) 0%, #ffffff 80%)",
    boxShadow: "0 4px 14px rgba(0, 51, 108, 0.12)",
  },
  "& .MuiPickersOutlinedInput-notchedOutline": {
    border: `2px solid ${BRAND_BLUE}`,
  },
  "& .MuiPickersOutlinedInput-root:hover .MuiPickersOutlinedInput-notchedOutline": {
    borderColor: BRAND_BLUE,
  },
  "& .MuiPickersOutlinedInput-root.Mui-focused .MuiPickersOutlinedInput-notchedOutline": {
    borderColor: BRAND_BLUE,
    borderWidth: 2,
  },
  "& .MuiSvgIcon-root": {
    color: BRAND_BLUE,
  },
};

const DATE_FILTER_POPPER_SX = {
  "& .MuiPaper-root": {
    border: `2px solid ${BRAND_BLUE}`,
    borderRadius: 2,
    boxShadow: "0 10px 24px rgba(0, 51, 108, 0.12)",
  },
  "& .MuiPickersCalendarHeader-label": {
    color: BRAND_BLUE,
    fontWeight: 700,
  },
  "& .MuiPickersArrowSwitcher-button": {
    color: BRAND_BLUE,
  },
  "& .MuiDayCalendar-weekDayLabel": {
    color: BRAND_BLUE,
    fontWeight: 700,
  },
  "& .MuiPickersDay-root": {
    color: BRAND_BLUE,
    fontWeight: 600,
  },
  "& .MuiPickersDay-root.Mui-selected": {
    backgroundColor: BRAND_BLUE,
    color: "#ffffff",
  },
  "& .MuiPickersDay-root.Mui-selected:hover": {
    backgroundColor: BRAND_BLUE,
  },
  "& .MuiPickersDay-root.MuiPickersDay-today": {
    border: `1px solid ${BRAND_BLUE}`,
  },
  "& .MuiPickersYear-yearButton.Mui-selected, & .MuiPickersMonth-monthButton.Mui-selected": {
    backgroundColor: BRAND_BLUE,
    color: "#ffffff",
  },
};

const CARD_SX = {
  border: "1px solid #e2e8f0",
  borderRadius: 3,
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
  background: "#ffffff",
  overflow: "hidden",
  transition: "box-shadow 0.15s ease, transform 0.15s ease",
  "&:hover": {
    boxShadow: "0 8px 20px rgba(15, 23, 42, 0.08)",
  },
};

const CHART_CARD_SX = {
  ...CARD_SX,
  border: `1.5px solid ${BRAND_BLUE}`,
};

// Validated categorical palette (CVD-safe order, see dataviz color-formula). Colors are
// assigned to categories by stable alphabetical label order below, never by post-sort
// array index, so a given tier/category keeps the same color across filters and re-renders.
const CATEGORICAL_PALETTE = ["#2a78d6", "#1baf7a", "#eda100", "#008300", "#4a3aa7", "#e34948", "#e87ba4", "#eb6834"];

// Single-hue magnitude ramp (light -> dark) for the renewal-month bar chart.
const SEQUENTIAL_BLUE = ["#86b6ef", "#5598e7", "#2a78d6", "#1c5cab", "#0d366b"];

const STATUS_COLORS = { good: "#0ca30c", warning: "#fab219", serious: "#ec835a", critical: "#d03b3b", muted: "#94a3b8" };

// These label sets are fixed by backend logic, so they can be mapped to semantic status
// colors directly rather than an arbitrary categorical order.
const SEMANTIC_COLOR_MAPS = {
  renewal_pipeline: {
    Overdue: STATUS_COLORS.critical,
    "Next 30 days": STATUS_COLORS.serious,
    "Next 90 days": STATUS_COLORS.warning,
    Future: STATUS_COLORS.good,
    "No renewal date": STATUS_COLORS.muted,
  },
  sponsorship_fulfillment_status: {
    prospect: "#6da7ec",
    committed: "#3987e5",
    invoiced: "#256abf",
    paid: "#184f95",
    fulfilled: "#0d366b",
    cancelled: STATUS_COLORS.critical,
  },
};

function stableCategoricalColorScale(labels) {
  const scale = new Map();
  Array.from(new Set(labels)).sort().forEach((label, index) => {
    scale.set(label, CATEGORICAL_PALETTE[index % CATEGORICAL_PALETTE.length]);
  });
  return scale;
}

function sequentialFill(count, max) {
  if (!max) return SEQUENTIAL_BLUE[0];
  const ratio = count / max;
  const index = Math.min(SEQUENTIAL_BLUE.length - 1, Math.round(ratio * (SEQUENTIAL_BLUE.length - 1)));
  return SEQUENTIAL_BLUE[index];
}

const AUTO_GRID = (min) => ({
  display: "grid",
  gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`,
  gap: "16px",
  alignItems: "stretch",
});

const CHART_GRID_SX = {
  display: "grid",
  gridTemplateColumns: {
    xs: "1fr",
    md: "repeat(2, minmax(0, 1fr))",
  },
  gap: "16px",
  alignItems: "stretch",
};

function SectionHeader({ title, subtitle }) {
  return (
    <Box sx={{ mb: 1.5, mt: 3.5, "&:first-of-type": { mt: 0 } }}>
      <Typography sx={{ color: BRAND_BLUE, fontWeight: 800, fontSize: 15, letterSpacing: 0.2 }}>
        {title}
      </Typography>
      {subtitle ? (
        <Typography sx={{ color: "#94a3b8", fontSize: 12.5, mt: 0.25 }}>{subtitle}</Typography>
      ) : null}
    </Box>
  );
}

const HEAD_CELL_SX = {
  bgcolor: "#f8fafc",
  color: "#64748b",
  fontSize: 11.5,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: 0.3,
  whiteSpace: "nowrap",
  borderBottom: "1px solid #e2e8f0",
};

const PAGE_CONFIG = {
  dashboard: {
    title: "Membership",
    label: "Dashboard",
    icon: CardMembershipIcon,
    status: "Active",
    description: "Membership health, renewal focus, upcoming activity, and operational shortcuts.",
  },
  events: {
    title: "Events",
    label: "Events",
    icon: CalendarMonthIcon,
    status: "Active",
    description: "Member events, attendance, registrations, and engagement touchpoints.",
  },
  sponsorships: {
    title: "Sponsorships",
    label: "Sponsorships",
    icon: HandshakeIcon,
    status: "Active",
    description: "Sponsor packages, commitments, renewal opportunities, and fulfillment status.",
  },
  reports: {
    title: "Reports",
    label: "Reports",
    icon: InsightsIcon,
    status: "Active",
    description: "Membership KPIs, retention reporting, revenue summaries, and board-ready exports.",
  },
  imports: {
    title: "Imports",
    label: "Imports",
    icon: FileUploadIcon,
    status: "Active",
    description: "Import workflows for member records, renewals, sponsorships, and event attendance.",
  },
};

const DASHBOARD_ACTIONS = [
  {
    title: "Members",
    description: "Open the current member company list and profiles.",
    path: "/membership/members",
    Icon: GroupsIcon,
  },
  {
    title: "Events",
    description: "Prepare event registration and attendance workflows.",
    path: "/membership/events",
    Icon: CalendarMonthIcon,
  },
  {
    title: "Sponsorships",
    description: "Track sponsor pipelines and fulfillment checkpoints.",
    path: "/membership/sponsorships",
    Icon: HandshakeIcon,
  },
  {
    title: "Reports",
    description: "Review retention, activity, and revenue reporting.",
    path: "/membership/reports",
    Icon: InsightsIcon,
  },
  {
    title: "Imports",
    description: "Stage member and activity import workflows.",
    path: "/membership/imports",
    Icon: FileUploadIcon,
  },
];

function MembershipHeader({ page, actions }) {
  const Icon = page.icon;
  const navigate = useNavigate();
  const [actionsAnchor, setActionsAnchor] = useState(null);

  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 5,
        borderBottom: "1px solid #dce4ef",
        background: "#ffffff",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          px: { xs: 2, md: 3 },
          py: 1.75,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 2,
              background: BRAND_GREEN,
              color: "#ffffff",
              display: "grid",
              placeItems: "center",
              flex: "0 0 auto",
            }}
          >
            <Icon sx={{ fontSize: 19 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 21,
                fontWeight: 800,
                color: BRAND_BLUE,
                lineHeight: 1.05,
              }}
            >
              {page.title}
            </Typography>
            <Typography sx={{ color: "#52677f", fontSize: 13, mt: 0.35 }}>
              {page.description}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: "0 0 auto" }}>
          {actions?.length ? (
            <>
              <Button
                endIcon={<KeyboardArrowDownIcon />}
                onClick={(event) => setActionsAnchor(event.currentTarget)}
                sx={ACTIONS_HEADER_BTN_SX}
              >
                Actions
              </Button>
              <Menu
                anchorEl={actionsAnchor}
                open={Boolean(actionsAnchor)}
                onClose={() => setActionsAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{ sx: ACTION_MENU_PAPER_SX }}
              >
                {actions.map((action) => (
                  <MenuItem
                    key={action.title}
                    onClick={() => {
                      setActionsAnchor(null);
                      navigate(action.path);
                    }}
                  >
                    <ListItemIcon sx={{ ...ACTION_MENU_ICON_SX, ...ACTION_MENU_ICON_TONES.export }}>
                      <action.Icon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{action.title}</ListItemText>
                  </MenuItem>
                ))}
              </Menu>
            </>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}

function MetricCard({ label, value, helper, Icon, accent = BRAND_BLUE }) {
  return (
    <Card sx={{ ...CARD_SX, position: "relative" }}>
      <Box sx={{ position: "absolute", top: 0, left: 0, width: 4, height: "100%", bgcolor: accent }} />
      <CardContent sx={{ p: 2.5, pl: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          <Typography sx={{ color: "#64748b", fontSize: 11.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3 }}>
            {label}
          </Typography>
          {Icon ? (
            <Box sx={{ width: 30, height: 30, borderRadius: 1.5, display: "grid", placeItems: "center", bgcolor: `${accent}14`, color: accent, flex: "0 0 auto" }}>
              <Icon sx={{ fontSize: 17 }} />
            </Box>
          ) : null}
        </Box>
        <Typography sx={{ color: BRAND_BLUE, fontSize: 30, fontWeight: 800, mt: 1, lineHeight: 1.1 }}>
          {value}
        </Typography>
        <Typography sx={{ color: "#94a3b8", fontSize: 12.5, mt: 0.75 }}>
          {helper}
        </Typography>
      </CardContent>
    </Card>
  );
}

function LoadingPattern({ label }) {
  return (
    <Card sx={CARD_SX}>
      <CardContent sx={{ p: 3 }}>
        <BrandSpinner compact label={label} py={3} />
        <Stack spacing={1.25} sx={{ mt: 1 }}>
          <Skeleton variant="rounded" height={14} />
          <Skeleton variant="rounded" height={14} width="82%" />
          <Skeleton variant="rounded" height={14} width="64%" />
        </Stack>
      </CardContent>
    </Card>
  );
}

function currency(value) {
  return Number(value || 0).toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function yearAgoIso() {
  const date = new Date();
  date.setFullYear(date.getFullYear() - 1);
  return date.toISOString().slice(0, 10);
}

function ChartTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload || {};
  const color = payload[0].color || row.fill || BRAND_BLUE;
  return (
    <Box sx={{ background: BRAND_BLUE, border: `1px solid ${BRAND_BLUE}`, borderRadius: 2, boxShadow: "0 10px 24px rgba(0,51,108,0.24)", px: 1.5, py: 1, minWidth: 120 }}>
      <Stack direction="row" alignItems="center" gap={0.75} sx={{ mb: 0.25 }}>
        <Box sx={{ width: 3, height: 14, borderRadius: 1, background: color, flexShrink: 0 }} />
        <Typography sx={{ fontSize: 12, color: "#ffffff", textTransform: "capitalize" }}>{row.label}</Typography>
      </Stack>
      <Typography sx={{ fontSize: 16, fontWeight: 800, color: "#ffffff" }}>{row.count}</Typography>
    </Box>
  );
}

function ChartLegend({ data, colorFor }) {
  return (
    <Stack direction="row" flexWrap="wrap" gap={1.25} sx={{ mt: 1.5, justifyContent: "center", px: 1, flexShrink: 0 }}>
      {data.map((entry) => (
        <Stack key={entry.label} direction="row" alignItems="center" gap={0.6}>
          <Box sx={{ width: 9, height: 9, borderRadius: "2px", background: colorFor(entry.label), flexShrink: 0 }} />
          <Typography sx={{ fontSize: 12, color: "#334155", textTransform: "capitalize" }}>
            {entry.label}
            <Typography component="span" sx={{ fontSize: 12, color: "#94a3b8", ml: 0.4 }}>({entry.count})</Typography>
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

function ChartCard({ title, data = [], type = "bar", colorKey = null, colorMode = "single", onSliceClick }) {
  const [hoverIndex, setHoverIndex] = useState(null);

  const colorFor = useMemo(() => {
    const semanticMap = colorKey ? SEMANTIC_COLOR_MAPS[colorKey] : null;
    if (semanticMap) return (label) => semanticMap[label] || STATUS_COLORS.muted;
    const scale = stableCategoricalColorScale(data.map((row) => row.label));
    return (label) => scale.get(label) || BRAND_BLUE;
  }, [colorKey, data]);

  const total = useMemo(() => data.reduce((sum, row) => sum + (row.count || 0), 0), [data]);
  const maxCount = useMemo(() => data.reduce((max, row) => Math.max(max, row.count || 0), 0), [data]);

  const barFillFor = (entry) => {
    if (colorMode === "sequential") return sequentialFill(entry.count, maxCount);
    if (colorMode === "categorical" || colorKey) return colorFor(entry.label);
    return BRAND_BLUE;
  };

  return (
    <Card sx={{ ...CHART_CARD_SX, height: "100%", display: "flex", flexDirection: "column" }}>
      <CardContent sx={{ p: 2.5, flex: 1, display: "flex", flexDirection: "column" }}>
        <Typography sx={{ color: BRAND_BLUE, fontWeight: 700, fontSize: 14.5, mb: 2 }}>{title}</Typography>
        {data.length ? (
          <Box sx={{ flex: 1, minHeight: { xs: 280, md: 340 }, display: "flex", flexDirection: "column", justifyContent: "center" }}>
            {type === "pie" ? (
              <>
                <Box sx={{ position: "relative", flex: "1 1 auto", minHeight: 160 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Tooltip content={<ChartTooltip />} />
                      <Pie
                        data={data}
                        dataKey="count"
                        nameKey="label"
                        innerRadius="56%"
                        outerRadius="88%"
                        paddingAngle={data.length > 1 ? 2 : 0}
                        cornerRadius={4}
                        onMouseEnter={(_entry, index) => setHoverIndex(index)}
                        onMouseLeave={() => setHoverIndex(null)}
                        onClick={(entry) => onSliceClick?.(entry)}
                        style={{ cursor: onSliceClick ? "pointer" : "default" }}
                      >
                        {data.map((entry, index) => (
                          <Cell
                            key={entry.label}
                            fill={colorFor(entry.label)}
                            opacity={hoverIndex === null || hoverIndex === index ? 1 : 0.35}
                          />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <Box sx={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", pointerEvents: "none" }}>
                    <Box sx={{ textAlign: "center" }}>
                      <Typography sx={{ fontSize: 22, fontWeight: 800, color: BRAND_BLUE, lineHeight: 1.1 }}>{total}</Typography>
                      <Typography sx={{ fontSize: 10.5, color: "#94a3b8", letterSpacing: 0.3 }}>TOTAL</Typography>
                    </Box>
                  </Box>
                </Box>
                <ChartLegend data={data} colorFor={colorFor} />
              </>
            ) : (
              <Box sx={{ flex: "1 1 auto", minHeight: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data} margin={{ top: 72, right: 28, left: 10, bottom: 18 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
                    <XAxis
                      dataKey="label"
                      angle={-45}
                      textAnchor="end"
                      interval={data.length > 8 ? Math.ceil(data.length / 8) - 1 : 0}
                      height={70}
                      tickMargin={18}
                      tick={{ fontSize: 12, fill: "#64748b" }}
                    />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#64748b" }} />
                    <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(0, 51, 108, 0.06)" }} />
                    <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={32}>
                      {data.map((entry) => (
                        <Cell key={entry.label} fill={barFillFor(entry)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Box>
        ) : (
          <Box sx={{ height: { xs: 280, md: 340 }, display: "grid", placeItems: "center" }}>
            <Typography sx={{ color: "#94a3b8", fontSize: 13.5, textAlign: "center" }}>No data for current filters.</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

function DataListCard({ title, rows = [], columns }) {
  return (
    <Card sx={{ ...CARD_SX, height: 340, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <CardContent sx={{ p: 2.5, flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        <Typography sx={{ color: BRAND_BLUE, fontWeight: 700, fontSize: 14.5, mb: 2 }}>{title}</Typography>
        {rows.length ? (
          <TableContainer sx={{ flex: 1, minHeight: 0, maxHeight: 258, overflowX: "auto", overflowY: "auto" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell key={column.key} sx={{ ...HEAD_CELL_SX, position: "sticky", top: 0, zIndex: 1 }}>{column.label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow key={row.id || index}>
                    {columns.map((column) => (
                      <TableCell key={column.key} sx={{ color: BRAND_BLUE, fontWeight: column.strong ? 800 : 500 }}>
                        {column.render ? column.render(row) : row[column.key] || "-"}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ flex: 1, minHeight: 160, display: "grid", placeItems: "center" }}>
            <Typography sx={{ color: "#94a3b8", fontSize: 13.5, textAlign: "center" }}>No members match this view.</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export function MembershipDashboard() {
  const page = PAGE_CONFIG.dashboard;
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    start: yearAgoIso(),
    end: todayIso(),
    member_tier: "",
    geography: "",
    membership_status: "",
    engagement_category: "",
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [members, setMembers] = useState([]);
  const [memberships, setMemberships] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/membership/members", { params: { limit: 500, offset: 0, sort_by: "name", sort_dir: "asc" } }),
      api.get("/membership/memberships", { params: { limit: 500, offset: 0 } }),
    ]).then(([memberRes, membershipRes]) => {
      setMembers(memberRes.data?.data || []);
      setMemberships(membershipRes.data?.data || []);
    }).catch(() => {});
  }, []);

  const filterOptions = useMemo(() => {
    const tierSet = new Set(memberships.map((row) => row.membership_level).filter(Boolean));
    const geoSet = new Set(members.flatMap((row) => [row.city, row.state, row.county, row.country]).filter(Boolean));
    const statusSet = new Set([...members.map((row) => row.status), ...memberships.map((row) => row.status)].filter(Boolean));
    const engagementSet = new Set(members.map((row) => row.engagement_category || "unscored").filter(Boolean));
    return {
      tiers: [...tierSet].sort(),
      geographies: [...geoSet].sort(),
      statuses: [...statusSet].sort(),
      engagement: [...engagementSet].sort(),
    };
  }, [members, memberships]);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, value]) => value));
      const response = await api.get("/membership/dashboard/overview", { params });
      setData(response.data);
    } catch {
      setError("Failed to load membership dashboard metrics.");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const updateFilter = (field) => (event) => {
    setFilters((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const updateDateFilter = (field) => (value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value?.isValid?.() ? value.format("YYYY-MM-DD") : "",
    }));
  };

  const cards = data?.cards || {};
  const charts = data?.charts || {};
  const lists = data?.lists || {};

  return (
    <Box sx={{ width: "100%", minHeight: "100vh", backgroundColor: PAGE_BG, pb: 4 }}>
      <MembershipHeader page={page} actions={DASHBOARD_ACTIONS} />

      <Box sx={{ px: { xs: 2, md: 3 }, mt: 3 }}>
        <Card sx={{ ...CARD_SX, mb: 3 }}>
          <CardContent sx={{ p: 2.5 }}>
            <Typography sx={{ color: "#94a3b8", fontSize: 11.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3, mb: 1.5 }}>
              Filters
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Start"
                  value={filters.start ? dayjs(filters.start) : null}
                  onChange={updateDateFilter("start")}
                  format="MM/DD/YYYY"
                  slotProps={{
                    textField: { size: "small", sx: DATE_FILTER_FIELD_SX },
                    popper: { sx: DATE_FILTER_POPPER_SX },
                  }}
                />
                <DatePicker
                  label="End"
                  value={filters.end ? dayjs(filters.end) : null}
                  onChange={updateDateFilter("end")}
                  format="MM/DD/YYYY"
                  slotProps={{
                    textField: { size: "small", sx: DATE_FILTER_FIELD_SX },
                    popper: { sx: DATE_FILTER_POPPER_SX },
                  }}
                />
              </LocalizationProvider>
              <TextField select label="Member Tier" value={filters.member_tier} onChange={updateFilter("member_tier")} size="small" sx={{ flex: 1, minWidth: 150 }}>
                <MenuItem value="">All tiers</MenuItem>
                {filterOptions.tiers.map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
              </TextField>
              <TextField select label="Geography" value={filters.geography} onChange={updateFilter("geography")} size="small" sx={{ flex: 1, minWidth: 150 }}>
                <MenuItem value="">All geography</MenuItem>
                {filterOptions.geographies.map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
              </TextField>
              <TextField select label="Membership Status" value={filters.membership_status} onChange={updateFilter("membership_status")} size="small" sx={{ flex: 1, minWidth: 150 }}>
                <MenuItem value="">All statuses</MenuItem>
                {filterOptions.statuses.map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
              </TextField>
              <TextField select label="Engagement" value={filters.engagement_category} onChange={updateFilter("engagement_category")} size="small" sx={{ flex: 1, minWidth: 150 }}>
                <MenuItem value="">All engagement</MenuItem>
                {filterOptions.engagement.map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
              </TextField>
            </Box>
          </CardContent>
        </Card>

        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
        {loading && !data ? <LoadingPattern label="Loading dashboard metrics..." /> : null}

        <SectionHeader title="Overview" />
        <Box sx={AUTO_GRID(190)}>
          <MetricCard label="Active Members" value={cards.active_members ?? "--"} helper="Current filtered active members." Icon={GroupsIcon} accent={BRAND_GREEN} />
          <MetricCard label="New Members" value={cards.new_members ?? "--"} helper="Joined in selected range." Icon={PersonAddAlt1Icon} accent={BRAND_GREEN} />
          <MetricCard label="At Risk" value={cards.at_risk_members ?? "--"} helper="Risk signals from renewal, touchpoints, and engagement." Icon={WarningAmberIcon} accent="#b7791f" />
          <MetricCard label="Recently Lapsed" value={cards.recently_lapsed ?? "--"} helper="Lapsed or expired members." Icon={ErrorOutlineIcon} accent="#be123c" />
          <MetricCard label="Sponsorship Revenue" value={cards.sponsorship_revenue == null ? "--" : currency(cards.sponsorship_revenue)} helper="Paid and fulfilled sponsorships." Icon={AttachMoneyIcon} accent={BRAND_BLUE} />
        </Box>

        <SectionHeader title="Trends & Distribution" subtitle="Membership, renewal, and engagement patterns for the selected filters." />
        <Box sx={CHART_GRID_SX}>
          <ChartCard
            title="Members by Tier"
            data={charts.members_by_tier}
            type="pie"
            onSliceClick={(entry) => {
              const label = entry?.label || entry?.payload?.label || entry?.name;
              if (!label) return;
              navigate(`/membership/members?membership_tier=${encodeURIComponent(label)}`);
            }}
          />
          <ChartCard title="Members by Renewal Month" data={charts.members_by_renewal_month} colorMode="sequential" />
          <ChartCard title="Renewal Pipeline" data={charts.renewal_pipeline} type="pie" colorKey="renewal_pipeline" />
          <ChartCard title="Event Attendance by Member Tier" data={charts.event_attendance_by_member_tier} colorMode="categorical" />
          <ChartCard title="Sponsorship Fulfillment Status" data={charts.sponsorship_fulfillment_status} type="pie" colorKey="sponsorship_fulfillment_status" />
          <ChartCard title="Engagement Score Distribution" data={charts.engagement_score_distribution} type="pie" />
        </Box>

        <SectionHeader title="Watchlists" subtitle="Members that need attention based on engagement, renewal, and attendance signals." />
        <Box sx={AUTO_GRID(380)}>
          <DataListCard
            title="Top Engaged Members"
            rows={lists.top_engaged_members}
            columns={[
              { key: "name", label: "Member", strong: true },
              { key: "score", label: "Score" },
              { key: "category", label: "Category" },
            ]}
          />
          <DataListCard
            title="At-Risk Members"
            rows={lists.at_risk_members}
            columns={[
              { key: "name", label: "Member", strong: true },
              { key: "score", label: "Score" },
              { key: "reasons", label: "Reasons", render: (row) => (row.reasons || []).join("; ") },
            ]}
          />
          <DataListCard
            title="Members With No Event Attendance in Past Year"
            rows={lists.members_no_event_attendance_past_year}
            columns={[
              { key: "name", label: "Member", strong: true },
              { key: "tier", label: "Tier" },
            ]}
          />
          <DataListCard
            title="High Investment, Low Participation"
            rows={lists.high_investment_low_participation}
            columns={[
              { key: "name", label: "Member", strong: true },
              { key: "investment", label: "Investment", render: (row) => currency(row.investment) },
              { key: "attendance_count", label: "Attendance" },
            ]}
          />
          <Box sx={{ gridColumn: "1 / -1" }}>
            <DataListCard
              title="Recently Lapsed Members"
              rows={lists.recently_lapsed_members}
              columns={[
                { key: "name", label: "Member", strong: true },
                { key: "status", label: "Status" },
                { key: "renewal_date", label: "Renewal Date" },
              ]}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export function MembershipPlaceholderPage({ type }) {
  const page = PAGE_CONFIG[type] || PAGE_CONFIG.dashboard;

  return (
    <Box sx={{ width: "100%", minHeight: "100vh", backgroundColor: PAGE_BG, pb: 4 }}>
      <MembershipHeader page={page} />

      <Box sx={{ px: { xs: 2, md: 3 }, mt: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} lg={8}>
            <Card sx={CARD_SX}>
              <CardContent sx={{ p: 3 }}>
                <Typography sx={{ color: BRAND_BLUE, fontSize: 18, fontWeight: 800 }}>
                  {page.label} Workspace
                </Typography>
                <Typography sx={{ color: "#52677f", fontSize: 14, lineHeight: 1.6, mt: 1 }}>
                  This page is wired into the Membership module shell and ready for the full workflow.
                </Typography>
                <Stack spacing={1.25} sx={{ mt: 3 }}>
                  <Skeleton variant="rounded" height={38} />
                  <Skeleton variant="rounded" height={38} />
                  <Skeleton variant="rounded" height={38} width="88%" />
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} lg={4}>
            <LoadingPattern label={`Loading ${page.label.toLowerCase()}...`} />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
